import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ImageIcon from '@mui/icons-material/Image';

interface FileInputTabProps {
  isProcessing: boolean;
  onProcessFiles: (files: File[], qaPairsCount: number) => void;
}

const FileInputTab: React.FC<FileInputTabProps> = ({ isProcessing, onProcessFiles }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [qaPairsCount, setQaPairsCount] = useState<number>(10); // Default to 10 pairs
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles(Array.from(event.target.files));
      setError(null);
    }
  };
  
  const handleRemoveFile = (indexToRemove: number) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };
    const handleProcessFiles = () => {
    if (!files.length) {
      setError('Please add at least one file');
      return;
    }
    
    setError(null);
    onProcessFiles(files, qaPairsCount);
  };
  const getIconForFile = (file: File) => {
    if (file.type === 'application/pdf') {
      return <PictureAsPdfIcon />;
    } else if (file.type.includes('text/')) {
      return <TextSnippetIcon />;
    } else if (file.type.startsWith('image/')) {
      return <ImageIcon />;
    } else {
      return <InsertDriveFileIcon />;
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload files to automatically generate Q&A pairs from their content using AI.
      </Typography>
      
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button 
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          startIcon={<UploadFileIcon />}
          disabled={isProcessing}
        >
          Upload Files
        </Button>
        <TextField
          label="Number of Q&A Pairs"
          type="number"
          size="small"
          value={qaPairsCount}
          onChange={(e) => setQaPairsCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
          inputProps={{ min: 1, max: 50 }}
          disabled={isProcessing}
          sx={{ width: '200px' }}
        />
        <input
          type="file"
          multiple
          accept=".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png,.gif,.avif"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
      
      {files.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, maxHeight: 200, overflow: 'auto' }}>
          <List dense>
            {files.map((file, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>
                    {getIconForFile(file)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={file.name} 
                    primaryTypographyProps={{ 
                      sx: { 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      } 
                    }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      aria-label="delete"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isProcessing}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < files.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleProcessFiles}
          disabled={files.length === 0 || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <InsertDriveFileIcon />}
        >
          {isProcessing ? 'Processing...' : 'Process Files'}
        </Button>
      </Box>
    </Box>
  );
};

export default FileInputTab;